import { useEffect, useState } from 'react';
import {
  Callout,
  Empty,
  Field,
  FilterBar,
  Loading,
  Modal,
  Select,
  TableScroll,
  formatDateTime,
  isApiClientError,
  useLoad,
} from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { Failure } from '../shared.tsx';
import {
  AUDIT_RECORD_KINDS,
  chainPosition,
  recordActionWords,
  recordActor,
  recordKindLabel,
  recordsQuery,
  type AuditRecordFilter,
  type AuditRecordRow,
} from './audit-records-model.ts';

/**
 * Audit chain → Records checked → the records an administrator should know
 * about and be able to verify: platform staff's own acts and the acts that
 * move money after the fact, by group, newest first, a page at a time.
 */
export function AuditRecordsDialog({ api, onClose }: { api: PlatformApi; onClose: () => void }) {
  const [kind, setKind] = useState<AuditRecordFilter>('all');
  const [version, setVersion] = useState(0);
  const [older, setOlder] = useState<AuditRecordRow[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const first = useLoad(() => api.auditRecords(recordsQuery({ kind })), [kind, version]);

  useEffect(() => {
    setOlder([]);
    setMoreError(null);
    setNext(first.state === 'ready' ? first.value.next_before : null);
  }, [first]);

  const rows: AuditRecordRow[] = first.state === 'ready' ? [...first.value.items, ...older] : [];

  const loadMore = () => {
    if (!next) return;
    setLoadingMore(true);
    setMoreError(null);
    api
      .auditRecords(recordsQuery({ kind, before: next }))
      .then((page) => {
        setOlder((o) => [...o, ...page.items]);
        setNext(page.next_before);
      })
      .catch(setMoreError)
      .finally(() => setLoadingMore(false));
  };

  return (
    <Modal title={t('integrity.records.title')} onClose={onClose} wide>
      <div className="stack">
        <p className="faint">{t('integrity.records.intro')}</p>
        <FilterBar>
          <Field label={t('integrity.records.group')}>
            <Select
              value={kind}
              onChange={(v) => setKind(v as AuditRecordFilter)}
              searchable={false}
              options={[
                { value: 'all', label: t('integrity.records.all') },
                ...AUDIT_RECORD_KINDS.map((k) => ({ value: k, label: recordKindLabel(k) })),
              ]}
            />
          </Field>
        </FilterBar>
        {first.state === 'loading' ? <Loading rows={4} /> : null}
        {first.state === 'error' ? (
          isApiClientError(first.error) && first.error.status === 403 ? (
            <Callout tone="caution" fold={false}>{t('integrity.accessLog.forbidden')}</Callout>
          ) : (
            <>
              <Failure error={first.error} />
              <div className="form-actions">
                <button type="button" className="copy" onClick={() => setVersion((v) => v + 1)}>
                  {t('integrity.accessLog.retry')}
                </button>
              </div>
            </>
          )
        ) : null}

        {first.state === 'ready' ? (
          rows.length === 0 ? (
            <Empty>{kind === 'all' ? t('integrity.records.empty') : t('integrity.records.emptyKind')}</Empty>
          ) : (
            <>
              <TableScroll>
                <table className="audit-records">
                  <thead>
                    <tr>
                      <th scope="col">{t('integrity.records.col.when')} · {t('integrity.records.col.chain')}</th>
                      <th scope="col">{t('integrity.records.col.action')}</th>
                      <th scope="col">{t('integrity.records.col.by')}</th>
                      <th scope="col">{t('common.operator')}</th>
                      <th scope="col">{t('common.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="date">
                          {formatDateTime(r.at)}
                          <div className="mono faint" title={t('integrity.records.col.chain')}>{chainPosition(r)}</div>
                        </td>
                        <td className="wrap">{recordActionWords(r)}</td>
                        <td>
                          {recordActor(r)}
                          {r.actor ? <div className="mono faint">{r.actor_role}</div> : null}
                        </td>
                        <td>{r.operator ?? (r.operator_id ? <span className="mono faint">{r.operator_id}</span> : <span className="faint">—</span>)}</td>
                        <td className="wrap">{r.reason ?? <span className="faint">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
              {moreError ? <Failure error={moreError} /> : null}
              <div className="form-actions">
                {next ? (
                  <button type="button" className="copy" disabled={loadingMore} onClick={loadMore}>
                    {loadingMore ? t('integrity.accessLog.loadingMore') : t('integrity.accessLog.more')}
                  </button>
                ) : (
                  <span className="faint">{t('integrity.accessLog.end')}</span>
                )}
              </div>
            </>
          )
        ) : null}
      </div>
    </Modal>
  );
}
